/**
 * Hook for managing word dictionary entries.
 * Supports global, case, and file scopes.
 */

import { useState, useEffect, useCallback } from 'react';
import { dictionaryApi, apiHelpers } from '../lib/api';
import type { WordDictionaryEntry } from '../types';

/**
 * Folds the entry the server returned from a create into the local list.
 *
 * POST /dictionaries/ is an upsert keyed on the word alone (see
 * `upsert_dictionary_rule`), so "creating" a word that already exists returns
 * the EXISTING row — same id, with `action` moved to the list you added it to.
 * Keeping `prev` in that case drops the server's update on the floor: the row
 * stays on its old tab and the add looks like it silently did nothing.
 */
export const mergeCreatedEntry = (
  prev: WordDictionaryEntry[],
  entry: WordDictionaryEntry
): WordDictionaryEntry[] =>
  prev.some((e) => e.id === entry.id)
    ? prev.map((e) => (e.id === entry.id ? entry : e))
    : [entry, ...prev];

interface UseDictionaryReturn {
  entries: WordDictionaryEntry[];
  isLoading: boolean;
  error: string | null;
  addEntry: (data: {
    word: string;
    action: 'always_redact' | 'always_allow';
    matchType?: 'exact' | 'contains';
    reason?: string;
  }) => Promise<WordDictionaryEntry | null>;
  updateEntry: (id: string, data: {
    word?: string;
    action?: 'always_redact' | 'always_allow';
    matchType?: 'exact' | 'contains';
    reason?: string;
  }) => Promise<WordDictionaryEntry | null>;
  deleteEntry: (id: string) => Promise<boolean>;
  refresh: () => Promise<void>;
}

export function useDictionary(
  scopeType: 'global' | 'case' | 'file',
  scopeId?: string
): UseDictionaryReturn {
  const [entries, setEntries] = useState<WordDictionaryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEntries = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await dictionaryApi.getEntries({
        scope_type: scopeType,
        scope_id: scopeId,
        // The server's max: an imported Allow List can run to thousands of entries.
        limit: 5000,
      });
      setEntries(response.entries);
    } catch (err) {
      const errorMessage = apiHelpers.handleError(err);
      setError(errorMessage);
      console.error('Failed to fetch dictionary entries:', errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [scopeType, scopeId]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const addEntry = useCallback(async (data: {
    word: string;
    action: 'always_redact' | 'always_allow';
    matchType?: 'exact' | 'contains';
    reason?: string;
  }): Promise<WordDictionaryEntry | null> => {
    setError(null);
    try {
      const newEntry = await dictionaryApi.createEntry({
        scope_type: scopeType,
        scope_id: scopeId,
        word: data.word,
        action: data.action,
        match_type: data.matchType ?? 'exact',
        reason: data.reason,
      });
      // A concurrent fetch can resolve after the create and already include the
      // new entry — prepending unconditionally would then show it twice.
      setEntries((prev) => mergeCreatedEntry(prev, newEntry));
      return newEntry;
    } catch (err) {
      const errorMessage = apiHelpers.handleError(err);
      setError(errorMessage);
      console.error('Failed to create dictionary entry:', errorMessage);
      return null;
    }
  }, [scopeType, scopeId]);

  const updateEntry = useCallback(async (id: string, data: {
    word?: string;
    action?: 'always_redact' | 'always_allow';
    matchType?: 'exact' | 'contains';
    reason?: string;
  }): Promise<WordDictionaryEntry | null> => {
    setError(null);
    try {
      const updated = await dictionaryApi.updateEntry(id, {
        word: data.word,
        action: data.action,
        match_type: data.matchType,
        reason: data.reason,
      });
      setEntries((prev) => prev.map((e) => (e.id === id ? updated : e)));
      return updated;
    } catch (err) {
      const errorMessage = apiHelpers.handleError(err);
      setError(errorMessage);
      console.error('Failed to update dictionary entry:', errorMessage);
      return null;
    }
  }, []);

  const deleteEntry = useCallback(async (id: string): Promise<boolean> => {
    setError(null);
    let snapshot: WordDictionaryEntry[] = [];
    setEntries((prev) => {
      snapshot = prev;
      return prev.filter((e) => e.id !== id);
    });

    try {
      await dictionaryApi.deleteEntry(id);
      return true;
    } catch (err) {
      setEntries(snapshot);
      const errorMessage = apiHelpers.handleError(err);
      setError(errorMessage);
      console.error('Failed to delete dictionary entry:', errorMessage);
      return false;
    }
  }, []);

  const refresh = useCallback(async () => {
    await fetchEntries();
  }, [fetchEntries]);

  return { entries, isLoading, error, addEntry, updateEntry, deleteEntry, refresh };
}
